import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  Tag,
  Space,
  Popconfirm,
  message,
  Tooltip,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  GiftOutlined,
  PlusOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  ThunderboltOutlined,
  DollarCircleOutlined,
  ReloadOutlined,
  BookOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import {
  getAllCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getAllCoursesByRole
} from '../../../Constants/Api/functions/function';
import { setAllCources } from '../../../Reducers/allCourcesReducer';

import './style.scss';

const { Option } = Select;

const Coupons = () => {
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const reduxCourses = useSelector((state) => state.allCources.allCources);
  const dispatch = useDispatch();

  const [coupons, setCoupons] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');

  // Live preview states for modal
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [currentDiscountAmount, setCurrentDiscountAmount] = useState(0);
  const [couponCode, setCouponCode] = useState('');

  const [form] = Form.useForm();

  const generateRandomCode = () => {
    const prefixes = ['SAVE', 'PROMO', 'IBAS', 'LEARN', 'DISC', 'BONUS', 'FEE'];
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `${prefix}${randomNum}`;
  };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await getAllCoupons();
      if (res && res.success) {
        setCoupons(res.coupons || []);
      } else {
        message.error(res?.message || 'Failed to fetch coupons');
      }
    } catch (err) {
      console.error('Error fetching coupons:', err);
      message.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const role = userProfile?.role || 'superadmin';
      const userId = userProfile?.id;
      const res = await getAllCoursesByRole(role, userId);
      if (Array.isArray(res)) {
        setCoursesList(res);
        dispatch(setAllCources(res));
      } else if (res && Array.isArray(res.cources)) {
        setCoursesList(res.cources);
        dispatch(setAllCources(res.cources));
      } else if (reduxCourses && reduxCourses.length > 0) {
        setCoursesList(reduxCourses);
      }
    } catch (err) {
      console.error('Error fetching courses list:', err);
      if (reduxCourses && reduxCourses.length > 0) {
        setCoursesList(reduxCourses);
      }
    }
  };

  useEffect(() => {
    fetchCoupons();
    fetchCourses();
  }, [userProfile]);

  const handleOpenCreateModal = () => {
    setEditingCoupon(null);
    setSelectedCourseId(null);
    setCurrentDiscountAmount(0);
    const initialCode = generateRandomCode();
    setCouponCode(initialCode);
    form.resetFields();
    form.setFieldsValue({
      code: initialCode,
      startDate: dayjs(),
      endDate: dayjs().add(30, 'day'),
      isActive: true,
      courseId: null,
      discountAmount: null
    });
    setModalVisible(true);
  };

  const handleOpenEditModal = (record) => {
    setEditingCoupon(record);
    setSelectedCourseId(record.courseId || null);
    setCurrentDiscountAmount(record.discountAmount || 0);
    setCouponCode(record.code || '');
    form.resetFields();
    form.setFieldsValue({
      code: record.code,
      discountAmount: record.discountAmount,
      startDate: dayjs(record.startDate),
      endDate: dayjs(record.endDate),
      courseId: record.courseId || null,
      maxUses: record.maxUses,
      isActive: Boolean(record.isActive)
    });
    setModalVisible(true);
  };

  const handleGenerateCode = () => {
    const newCode = generateRandomCode();
    setCouponCode(newCode);
    form.setFieldsValue({ code: newCode });
    message.success(`Generated new coupon code: ${newCode}`);
  };

  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        code: values.code.trim().toUpperCase(),
        discountAmount: Number(values.discountAmount),
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
        courseId: values.courseId || null,
        maxUses: values.maxUses ? Number(values.maxUses) : null,
        isActive: values.isActive ? 1 : 0,
        userId: userProfile?.id
      };

      let res;
      if (editingCoupon) {
        res = await updateCoupon(editingCoupon.id, payload);
      } else {
        res = await createCoupon(payload);
      }

      if (res && res.success) {
        message.success(editingCoupon ? 'Coupon updated successfully!' : 'Coupon created successfully!');
        setModalVisible(false);
        form.resetFields();
        fetchCoupons();
      } else {
        message.error(res?.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      message.error('An error occurred while saving coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCoupon = async (id) => {
    try {
      const res = await deleteCoupon(id);
      if (res && res.success) {
        message.success('Coupon deleted successfully');
        fetchCoupons();
      } else {
        message.error(res?.message || 'Failed to delete coupon');
      }
    } catch (err) {
      console.error('Delete coupon error:', err);
      message.error('Failed to delete coupon');
    }
  };

  const handleToggleActive = async (record, checked) => {
    try {
      const res = await updateCoupon(record.id, {
        ...record,
        isActive: checked ? 1 : 0
      });
      if (res && res.success) {
        message.success(`Coupon ${checked ? 'activated' : 'deactivated'} successfully`);
        fetchCoupons();
      } else {
        message.error(res?.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Toggle error:', err);
      message.error('Failed to update status');
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    message.success(`Coupon code "${code}" copied to clipboard!`);
  };

  // Metrics Calculation
  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter((c) => c.status === 'active').length;
  const totalRedemptions = coupons.reduce((sum, c) => sum + (Number(c.usedCount) || 0), 0);
  const totalDiscountGiven = coupons.reduce(
    (sum, c) => sum + (Number(c.discountAmount) || 0) * (Number(c.usedCount) || 0),
    0
  );

  // Filter Data
  const filteredCoupons = coupons.filter((c) => {
    const matchesSearch =
      c.code.toLowerCase().includes(searchText.toLowerCase()) ||
      (c.courseName && c.courseName.toLowerCase().includes(searchText.toLowerCase()));

    const matchesStatus = statusFilter === 'all' ? true : c.status === statusFilter;
    const matchesCourse = courseFilter === 'all'
      ? true
      : courseFilter === 'global'
      ? !c.courseId
      : c.courseId === courseFilter;

    return matchesSearch && matchesStatus && matchesCourse;
  });

  const columns = [
    {
      title: 'Coupon Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => (
        <Tooltip title="Click to copy coupon code">
          <span className="coupon-code-pill" onClick={() => handleCopyCode(code)}>
            <GiftOutlined />
            {code}
            <CopyOutlined style={{ fontSize: 12, color: '#0284c7' }} />
          </span>
        </Tooltip>
      )
    },
    {
      title: 'Discount (LKR)',
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      render: (amount) => (
        <span className="discount-badge">
          - {Number(amount).toLocaleString()} LKR
        </span>
      ),
      sorter: (a, b) => a.discountAmount - b.discountAmount
    },
    {
      title: 'Validity Period',
      key: 'validity',
      render: (_, record) => {
        const start = dayjs(record.startDate).format('MMM DD, YYYY');
        const end = dayjs(record.endDate).format('MMM DD, YYYY');
        return (
          <span style={{ fontSize: '0.875rem', color: '#475569' }}>
            {start} <span style={{ color: '#94a3b8' }}>→</span> {end}
          </span>
        );
      }
    },
    {
      title: 'Selected Course',
      dataIndex: 'courseName',
      key: 'courseName',
      render: (courseName, record) => {
        if (!record.courseId) {
          return <Tag color="geekblue">🌐 All Courses (Global)</Tag>;
        }
        return (
          <Tag color="cyan" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <BookOutlined style={{ marginRight: 4 }} />
            {courseName || `Course #${record.courseId}`}
          </Tag>
        );
      }
    },
    {
      title: 'Redemptions',
      key: 'usage',
      render: (_, record) => {
        const used = record.usedCount || 0;
        const max = record.maxUses;
        return (
          <span style={{ fontWeight: 600, color: '#334155' }}>
            {used} / {max ? max : '∞'}
          </span>
        );
      },
      sorter: (a, b) => (a.usedCount || 0) - (b.usedCount || 0)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        switch (status) {
          case 'active':
            return <Tag color="success">Active</Tag>;
          case 'upcoming':
            return <Tag color="warning">Upcoming</Tag>;
          case 'expired':
            return <Tag color="error">Expired</Tag>;
          case 'depleted':
            return <Tag color="purple">Depleted (Max Limit)</Tag>;
          case 'disabled':
          default:
            return <Tag color="default">Deactivated</Tag>;
        }
      }
    },
    {
      title: 'Active',
      key: 'isActive',
      render: (_, record) => (
        <Switch
          checked={Boolean(record.isActive)}
          onChange={(checked) => handleToggleActive(record, checked)}
          size="small"
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit coupon">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: '#1677ff' }} />}
              onClick={() => handleOpenEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this coupon?"
            okText="Yes, Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDeleteCoupon(record.id)}
          >
            <Tooltip title="Delete coupon">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const selectedCourseObj = coursesList.find((c) => c.id === selectedCourseId);

  return (
    <div className="coupons-management-container">
      {/* Header Banner */}
      <div className="coupons-header-glass">
        <div className="header-info">
          <h1>
            <GiftOutlined /> Promotional <span className="highlight">Coupons Manager</span>
          </h1>
          <p>
            Create and manage discount coupons for student course enrollments.
            Select a target course or apply globally, set start/end dates, enter the discount amount, and manage redemption limits.
          </p>
        </div>
        <div className="header-action">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreateModal}
            className="create-btn"
          >
            Create / Generate Coupon
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <Row gutter={[16, 16]} className="metrics-row">
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card blue" bordered={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Statistic title="Total Coupons" value={totalCoupons} />
              <div className="metric-icon-box">
                <GiftOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card green" bordered={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Statistic title="Active Coupons" value={activeCoupons} />
              <div className="metric-icon-box">
                <CheckCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card purple" bordered={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Statistic title="Total Redemptions" value={totalRedemptions} />
              <div className="metric-icon-box">
                <ThunderboltOutlined />
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="metric-card amber" bordered={false}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Statistic
                title="Total Discounts Given"
                value={totalDiscountGiven}
                suffix="LKR"
                formatter={(val) => Number(val).toLocaleString()}
              />
              <div className="metric-icon-box">
                <DollarCircleOutlined />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table & Filters Card */}
      <Card className="table-card" bordered={false}>
        <div className="filter-bar">
          <Space wrap>
            <Input
              placeholder="Search coupon code..."
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              className="search-input"
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="status-select"
            >
              <Option value="all">All Statuses</Option>
              <Option value="active">Active</Option>
              <Option value="upcoming">Upcoming</Option>
              <Option value="expired">Expired</Option>
              <Option value="depleted">Depleted</Option>
              <Option value="disabled">Deactivated</Option>
            </Select>

            <Select
              value={courseFilter}
              onChange={setCourseFilter}
              style={{ minWidth: 220 }}
              placeholder="Filter by Course"
            >
              <Option value="all">All Courses (Filter)</Option>
              <Option value="global">Global Coupons Only</Option>
              {coursesList.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.name}
                </Option>
              ))}
            </Select>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={fetchCoupons} loading={loading}>
            Refresh
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={filteredCoupons}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <GiftOutlined style={{ color: '#0284c7' }} />
            <span>{editingCoupon ? 'Edit Coupon' : 'Create / Generate Promotional Coupon'}</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        centered
        className="coupon-modal"
        width={580}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{ isActive: true }}
        >
          {/* 1. SELECT COURSE */}
          <Form.Item
            name="courseId"
            label={
              <span style={{ fontWeight: 600 }}>
                <BookOutlined style={{ marginRight: 6, color: '#0284c7' }} />
                Select Course
              </span>
            }
            extra="Select a specific course to restrict this coupon, or select 'All Courses (Global)'."
          >
            <Select
              placeholder="Search or Select Course (or All Courses)"
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(val) => setSelectedCourseId(val)}
              options={[
                { value: null, label: '🌐 All Courses (Global Coupon)' },
                ...coursesList.map((c) => ({
                  value: c.id,
                  label: `${c.name} ${c.courseid ? `(${c.courseid})` : ''} - Standard: ${Number(c.price || 0).toLocaleString()} LKR`
                }))
              ]}
            />
          </Form.Item>

          {/* 2. COUPON CODE */}
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>
                  <GiftOutlined style={{ marginRight: 6, color: '#0284c7' }} />
                  Coupon Code
                </span>
                {couponCode && (
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Active Code: <strong style={{ color: '#0284c7', letterSpacing: 1, fontFamily: 'monospace' }}>{couponCode}</strong>
                  </span>
                )}
              </div>
            }
            required
            extra="Letters and numbers only. You can type your own or click 'Generate Code' to auto-generate a unique code."
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <Form.Item
                name="code"
                noStyle
                rules={[
                  { required: true, message: 'Please enter or generate a coupon code' },
                  { min: 3, message: 'Code must be at least 3 characters' }
                ]}
              >
                <Input
                  value={couponCode}
                  placeholder="e.g. SUMMER5000"
                  size="large"
                  style={{
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    fontSize: '15px',
                    fontFamily: 'monospace',
                    color: '#03245e',
                    background: '#f8fafc',
                    borderColor: '#cbd5e1'
                  }}
                  onChange={(e) => {
                    const upper = e.target.value.toUpperCase();
                    setCouponCode(upper);
                    form.setFieldsValue({ code: upper });
                  }}
                />
              </Form.Item>
              <Button
                type="primary"
                size="large"
                icon={<ThunderboltOutlined />}
                onClick={handleGenerateCode}
                className="generate-code-btn"
                style={{
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  borderColor: '#0284c7',
                  color: '#ffffff',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  padding: '0 18px',
                  height: 40
                }}
              >
                Generate Code
              </Button>
            </div>
          </Form.Item>

          {/* 3. DISCOUNT AMOUNT */}
          <Form.Item
            name="discountAmount"
            label="Discount Amount (LKR)"
            rules={[
              { required: true, message: 'Please specify the discount amount in LKR' },
              {
                type: 'number',
                min: 1,
                message: 'Discount amount must be at least 1 LKR'
              }
            ]}
            extra="This amount will be deducted directly from the course standard price."
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="e.g. 5000"
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
              min={1}
              onChange={(val) => setCurrentDiscountAmount(val || 0)}
            />
          </Form.Item>

          {/* Live Discount Calculation Preview if course is selected */}
          {selectedCourseObj && (
            <div style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              padding: '12px 16px',
              borderRadius: 8,
              marginBottom: 16
            }}>
              <div style={{ color: '#166534', fontWeight: 600, marginBottom: 6, fontSize: '0.9rem' }}>
                Course Pricing Preview:
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#374151', marginBottom: 2 }}>
                <span>Standard Price:</span>
                <span>{Number(selectedCourseObj.price || 0).toLocaleString()} LKR</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#dc2626', marginBottom: 2 }}>
                <span>Coupon Discount:</span>
                <span>- {Number(currentDiscountAmount || 0).toLocaleString()} LKR</span>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.9rem',
                color: '#059669',
                fontWeight: 700,
                borderTop: '1px dashed #86efac',
                paddingTop: 6,
                marginTop: 4
              }}>
                <span>Student Final Price:</span>
                <span>{Math.max(0, Number(selectedCourseObj.price || 0) - Number(currentDiscountAmount || 0)).toLocaleString()} LKR</span>
              </div>
            </div>
          )}

          {/* 4. VALIDITY DATES */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="startDate"
                label="Start Date"
                rules={[{ required: true, message: 'Please select start date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="endDate"
                label="End Date"
                rules={[{ required: true, message: 'Please select end date' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          {/* 5. USAGE LIMITS & ACTIVE STATUS */}
          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item
                name="maxUses"
                label="Maximum Usages (Optional)"
                extra="Leave empty for unlimited redemptions."
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="e.g. 50 (unlimited if empty)"
                  min={1}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item
                name="isActive"
                label="Coupon Status"
                valuePropName="checked"
                extra="Active coupons can be redeemed immediately."
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <Button onClick={() => setModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {editingCoupon ? 'Save Changes' : 'Create Coupon'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Coupons;
